import styled, {css, keyframes} from "styled-components";
import {eyebrowTextCss} from "theme/typography";
import {brand, fontFamilies, fontWeights, radii} from "theme/tokens";

/**
 * Externalized styles for the authenticated support-request dialog
 * (kitecorp/transferify-api-service#101, owner externalized-styles rule 2026-07-12).
 * styled-components rather than a CSS Module because the surface is theme-aware: every color reads
 * `theme.tokens.*`, so light + dark parity falls out of the token layer for free.
 *
 * It echoes the feedback dialog's editorial anatomy (mono eyebrow · serif title with an italic accent ·
 * lede) on purpose — they are sibling support surfaces and should feel related — but keeps its own
 * file rather than importing that one: they belong to different features (public `/feedback` versus
 * authenticated `/support/request`) and a restyle of one must not silently restyle the other.
 *
 * The component file stays markup + behaviour only; no colors are hardcoded here — tokens only.
 */

/** Positioning context inside the MUI paper so the ✕ can anchor to the top-right of the sheet. */
export const Sheet = styled.div`
	position: relative;
`;

/** The editorial header block (mono eyebrow + serif title + lede), on a card surface. */
export const Head = styled.div`
	position: relative;
	text-align: center;
	padding: 34px 40px 22px;
	border-bottom: 1px solid ${({theme}) => theme.tokens.line};

	@media (max-width: 600px) {
		padding: 28px 24px 18px;
	}
`;

export const HeadInner = styled.div`
	max-width: 420px;
	margin: 0 auto;
`;

/** Top-right ✕, mirrored from the other sheets so the dialogs feel related. */
export const CloseButton = styled.button`
	position: absolute;
	top: 16px;
	right: 16px;
	/* Above the later positioned <Head> sibling — without this the transparent Head hit-tests over the
	   ✕ and swallows its clicks (the ui#158 failure). */
	z-index: 3;
	width: 40px;
	height: 40px;
	border-radius: 11px;
	border: none;
	background: ${({theme}) => theme.tokens.field};
	color: ${({theme}) => theme.tokens.ink2};
	cursor: pointer;
	font-size: 16px;
	line-height: 1;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition: background 0.14s ease, color 0.14s ease;

	&:hover {
		background: ${({theme}) => theme.tokens.line};
		color: ${({theme}) => theme.tokens.ink};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/** Mono uppercase eyebrow (reads `segInk` in dark, matching the other sheets). */
export const Eyebrow = styled.div`
	${eyebrowTextCss};
	font-weight: 700;
	letter-spacing: 0.1em;
	color: ${({theme}) => (theme.tokens.mode === "dark" ? theme.tokens.segInk : theme.tokens.blue)};
`;

/** Instrument-Serif display title, sized for the compact dialog. */
export const Title = styled.h2`
	margin: 9px 0 0;
	font-family: ${fontFamilies.displaySerif};
	font-weight: ${fontWeights.display};
	font-size: clamp(30px, 6vw, 40px);
	line-height: 1.04;
	letter-spacing: -0.015em;
	color: ${({theme}) => theme.tokens.ink};
`;

/** Italic accent word inside the title. */
export const TitleAccent = styled.span`
	font-style: italic;
	color: ${({theme}) => (theme.tokens.mode === "dark" ? theme.tokens.segInk : theme.tokens.blue)};
`;

/** Supportive lede under the title. */
export const Lede = styled.p`
	margin: 11px auto 0;
	max-width: 380px;
	font-size: 15px;
	line-height: 1.55;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * The SLA promise, rendered only for a plan carrying the `prioritySupport` capability. Gold tint and
 * `ink`, the same premium accent language the paid plan chip speaks, so the entitlement reads as part
 * of the subscription rather than as a generic notice — and `ink` rather than `ink2` because a promise
 * the company is making should not be styled as secondary text.
 */
export const SlaNote = styled.p`
	margin: 14px auto 0;
	max-width: 380px;
	padding: 9px 14px;
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.goldTint};
	font-size: 13.5px;
	line-height: 1.45;
	color: ${({theme}) => theme.tokens.ink};
`;

/**
 * Where the answer will go (ui#1085), stated under the fields.
 *
 * Deliberately QUIETER than {@link SlaNote}: the SLA is a promise the company is making and earns the
 * accented surface, while this is orientation — `ink2` secondary text with no fill, so it informs
 * without competing with the entitlement above it or with the submit button beside it.
 */
export const ReplyToNote = styled.p`
	margin: 0;
	font-size: 13px;
	line-height: 1.45;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** The content region below the head. */
export const Body = styled.div`
	padding: 22px 30px 26px;

	@media (max-width: 600px) {
		padding: 20px 20px 22px;
	}
`;

/**
 * A REAL form element, not a styled div: it is what makes Enter-to-send work and what gives the
 * primary action a `type="submit"` to be the default button of.
 */
export const Form = styled.form`
	margin: 0;
`;

/** Vertical stack of labeled fields. */
export const Fields = styled.div`
	display: flex;
	flex-direction: column;
	gap: 18px;
`;

export const Field = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

/**
 * Live length counter under the message field. Turns `errorInk` at the limit, which is the point the
 * user needs to notice — input stops being accepted there.
 *
 * `ink2`, not `ink3`: ink3 is the disabled/decorative tier and fails WCAG AA as readable text
 * (2.62:1 light / 3.71:1 dark), the same reason the field hints read ink2 (ui#302).
 */
export const MessageCounter = styled.span<{$atLimit: boolean}>`
	align-self: flex-end;
	font-size: 12px;
	font-variant-numeric: tabular-nums;
	color: ${({theme, $atLimit}) => ($atLimit ? theme.tokens.errorInk : theme.tokens.ink2)};
`;

/**
 * Per-field validation line, sitting under the field it belongs to. Distinct from {@link FailureBlock}:
 * this is something the user can fix, so it is anchored to the input that is wrong rather than
 * floated above the actions as a verdict on the whole request.
 */
export const FieldError = styled.p`
	margin: 0;
	padding-inline-start: 2px;
	font-size: 12.5px;
	line-height: 1.4;
	color: ${({theme}) => theme.tokens.errorInk};
`;

/**
 * The outcome of a failed send, kept above the actions with the typed request preserved beneath it.
 * A tinted block rather than a bare line because it can carry the fallback address as a second
 * element, and a loose link under loose text reads as unrelated to the failure it belongs to.
 */
export const FailureBlock = styled.div`
	margin-top: 18px;
	padding: 12px 14px;
	border-radius: ${radii.control};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	background: ${({theme}) => theme.tokens.field};
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

export const FailureText = styled.p`
	margin: 0;
	font-size: 13.5px;
	line-height: 1.45;
	color: ${({theme}) => theme.tokens.errorInk};
`;

/**
 * The direct `mailto:` escape hatch offered with an undeliverable request. Deliberately prominent —
 * it is the only route left to a user whose request our own transport could not carry.
 */
export const MailtoLink = styled.a`
	align-self: flex-start;
	font-size: 14px;
	font-weight: 700;
	line-height: 1.4;
	color: ${({theme}) => theme.tokens.linkInk};
	text-decoration: underline;
	text-underline-offset: 2px;

	&:hover {
		color: ${({theme}) => theme.tokens.linkHover};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
		border-radius: 4px;
	}
`;

/** Footer actions: ghost Cancel + primary Send. Full-width stack on small screens. */
export const Actions = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 10px;
	margin-top: 22px;

	@media (max-width: 600px) {
		flex-direction: column-reverse;
		align-items: stretch;
	}
`;

const buttonBase = css`
	height: 46px;
	min-width: 120px;
	padding: 0 20px;
	border-radius: ${radii.control};
	font-family: inherit;
	font-weight: 700;
	font-size: 15px;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 9px;
	transition: background 0.16s ease, filter 0.16s ease, color 0.16s ease, opacity 0.16s ease;

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}

	@media (max-width: 600px) {
		width: 100%;
	}
`;

/** Subtle secondary action. */
export const GhostButton = styled.button`
	${buttonBase};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	background: transparent;
	color: ${({theme}) => theme.tokens.ink2};

	&:hover {
		background: ${({theme}) => theme.tokens.field};
		color: ${({theme}) => theme.tokens.ink};
	}
`;

/** Primary CTA in the landing button language (filled brand blue). */
export const SubmitButton = styled.button`
	${buttonBase};
	border: none;
	background: ${({theme}) => theme.tokens.actionFill};
	color: ${({theme}) => theme.tokens.actionInk};

	&:hover:not(:disabled):not([aria-disabled="true"]) {
		background: ${({theme}) => theme.tokens.actionFillHover};
	}

	/* A send in flight marks the button aria-disabled rather than disabled, so it keeps keyboard focus
	   (kitecorp/transferify-ui#3076). It looks exactly as unavailable as a disabled one. */
	&:disabled,
	&[aria-disabled="true"] {
		cursor: default;
		opacity: 0.55;
	}
`;

const spin = keyframes`
	to {
		transform: rotate(360deg);
	}
`;

/**
 * In-button spinner shown while a send is in flight. It sits on the filled blue CTA, so its arc is
 * white and its track a translucent white — the treatment the design encodes for surfaces layered on
 * a colored fill (see `brand.premInk*`).
 */
export const Spinner = styled.span`
	width: 16px;
	height: 16px;
	border-radius: 50%;
	border: 2px solid ${brand.premInk3};
	border-top-color: ${brand.white};
	animation: ${spin} 0.7s linear infinite;
`;

/** The calm designed success moment that replaces the form after a request is sent. */
export const Success = styled.div`
	text-align: center;
	padding: 8px 6px 6px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
`;

/** Teal check disc above the success title. */
export const SuccessMark = styled.div`
	width: 56px;
	height: 56px;
	border-radius: 50%;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 26px;
	color: ${({theme}) => theme.tokens.teal};
	background: ${({theme}) => theme.tokens.signalTint};
`;

export const SuccessTitle = styled.h3`
	margin: 0;
	font-family: ${fontFamilies.displaySerif};
	font-weight: ${fontWeights.display};
	font-size: clamp(28px, 5.5vw, 34px);
	line-height: 1.05;
	letter-spacing: -0.01em;
	color: ${({theme}) => theme.tokens.ink};
`;

export const SuccessNote = styled.p`
	margin: 0;
	max-width: 340px;
	font-size: 15px;
	line-height: 1.55;
	color: ${({theme}) => theme.tokens.ink2};
`;
